import careConfig from "@careConfig";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { navigate } from "raviger";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Avatar } from "@/components/Common/Avatar";
import { CardGridSkeleton } from "@/components/Common/SkeletonLoading";

import { usePatientSignOut } from "@/hooks/usePatientSignOut";

import { LocalStorageKeys } from "@/common/constants";

import query from "@/Utils/request/query";
import { TokenData } from "@/types/otp/otp";
import PublicAppointmentApi from "@/types/scheduling/PublicAppointmentApi";

const { customLogo, stateLogo, mainLogo } = careConfig;

export function LandingPage() {
  const { t } = useTranslation();
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(
    null,
  );
  const signOut = usePatientSignOut();
  const tokenData: TokenData = JSON.parse(
    localStorage.getItem(LocalStorageKeys.patientTokenKey) || "{}",
  );

  const isLoggedIn =
    tokenData.token &&
    Object.keys(tokenData).length > 0 &&
    dayjs(tokenData.createdAt).isAfter(dayjs().subtract(14, "minutes"));

  // Sistemos specialybių katalogas — pacientas renkasi specialybę, tada rodom
  // klinikas su tos specialybės specialistais.
  const { data: specialtiesResponse, isLoading: specialtiesLoading } = useQuery(
    {
      queryKey: ["public-specialties"],
      queryFn: query(PublicAppointmentApi.getPublicSpecialties),
    },
  );
  const specialties = specialtiesResponse?.results ?? [];

  // Pasirinkus specialybę — įstaigos, turinčios tos specialybės specialistų.
  const { data: facilitiesResponse, isFetching: facilitiesFetching } = useQuery(
    {
      queryKey: ["facilities-by-specialty", selectedSpecialty],
      queryFn: query(PublicAppointmentApi.getFacilitiesBySpecialty, {
        queryParams: { specialty: selectedSpecialty ?? "" },
      }),
      enabled: !!selectedSpecialty,
    },
  );

  // Kai gauname įstaigas: viena → iškart specialistų sąrašas; kelios → sąrašas.
  useEffect(() => {
    if (!selectedSpecialty || facilitiesFetching || !facilitiesResponse) return;
    const facilities = facilitiesResponse.results;
    if (facilities.length === 0) {
      toast.error(t("no_specialists"));
      setSelectedSpecialty(null);
      return;
    }
    if (facilities.length === 1) {
      navigate(
        `/facility/${facilities[0].id}?specialty=${encodeURIComponent(selectedSpecialty)}`,
      );
    } else {
      navigate(
        `/facilities?specialty=${encodeURIComponent(selectedSpecialty)}`,
      );
    }
    setSelectedSpecialty(null);
  }, [facilitiesResponse, facilitiesFetching, selectedSpecialty, t]);

  return (
    <div className="min-h-screen flex flex-col p-5">
      {/* Main Content  */}
      {isLoggedIn && (
        <header className="w-full">
          <div className="flex justify-end items-center gap-2">
            <Button
              variant="ghost"
              className="text-sm font-medium hover:bg-gray-100 px-6"
              onClick={() => navigate("/patient/home")}
            >
              {t("home")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Avatar name={"User"} className="size-7 rounded-full" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-xs text-gray-500">
                  <span className="font-medium">{tokenData.phoneNumber}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                  onClick={signOut}
                >
                  <CareIcon icon="l-signout" className="mr-2 size-4" />
                  {t("sign_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
      )}
      <main className="lg:flex-1 flex flex-col items-center justify-center py-4 md:py-8">
        {/* Logo Section */}
        <div className="w-full flex flex-col items-center mt-2 md:mt-0">
          {stateLogo && stateLogo.dark && (
            <div className="mb-2">
              <img
                src={stateLogo.dark}
                alt="Logo"
                className="h-20 md:h-28 w-auto"
              />
            </div>
          )}

          {(customLogo || mainLogo) && (
            <div className="mb-4 md:mb-8">
              <img
                src={customLogo?.dark ?? mainLogo?.dark}
                alt="Logo"
                className="h-16 md:h-20 w-auto"
              />
            </div>
          )}
        </div>

        {/* Specialty picker PIRMAS — vizito užsakymas (naujiems pacientams) */}
        <div className="w-full max-w-[620px] mx-auto px-4 sm:px-6 py-4 bg-gray-100 rounded-md">
          <div className="text-center mb-4">
            <span className="text-sm md:text-base font-medium">
              {t("choose_specialty")}
            </span>
          </div>
          {specialtiesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CardGridSkeleton count={4} />
            </div>
          ) : specialties.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-4">
              {t("no_specialties_found")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {specialties.map((specialty) => {
                const isBusy =
                  facilitiesFetching && selectedSpecialty === specialty.key;
                return (
                  <button
                    key={specialty.key}
                    type="button"
                    disabled={facilitiesFetching}
                    onClick={() => setSelectedSpecialty(specialty.key)}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition-all hover:border-primary-400 hover:shadow-md disabled:opacity-60"
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                      {isBusy ? (
                        <CareIcon
                          icon="l-spinner"
                          className="size-5 animate-spin"
                        />
                      ) : (
                        <CareIcon icon="l-stethoscope" className="size-5" />
                      )}
                    </span>
                    <span className="font-medium">{specialty.name_lt}</span>
                    <CareIcon
                      icon="l-arrow-right"
                      className="ml-auto size-4 text-gray-400"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Centered Dots Image */}
        <div className="flex justify-center my-6 md:my-8">
          <img src="/images/dots.svg" alt="" />
        </div>

        {/* Prisijungimas — esamiems naudotojams (pacientams / personalui) */}
        {!isLoggedIn && (
          <div className="w-full max-w-[620px] flex flex-col items-center justify-center bg-gray-100 p-4 rounded-lg">
            <div className="text-sm font-medium mb-4 md:mb-6 text-center">
              {t("login_already_registered")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 w-full max-w-full justify-center">
              <div className="flex flex-col items-center justify-center gap-5 p-3 rounded-xl shadow-sm bg-white hover:shadow-md transition-all bg-[url('/images/patient_background.png')] bg-auto bg-center bg-no-repeat">
                <div className="rounded-full bg-indigo-100 m-2 p-1 aspect-square flex justify-center items-center border-2 border-white shadow-sm">
                  <CareIcon
                    icon="d-patient"
                    className="size-8 text-indigo-700"
                  />
                </div>
                <div className="flex flex-col items-center">
                  <Button
                    variant="outline"
                    className="w-full text-xs md:text-sm border border-primary-600 text-primary-700 hover:text-primary-800 font-semibold"
                    onClick={() => navigate(`/login?mode=patient`)}
                  >
                    {t("patient_login")}
                  </Button>
                  <p className="text-xs mt-2 w-full text-center">
                    {t("patient_login_description")}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center gap-5 p-3 rounded-xl shadow-sm bg-white hover:shadow-md transition-all bg-[url('/images/staff_background.png')] bg-auto bg-center bg-no-repeat">
                <div className="rounded-full bg-green-100 m-2 p-1 aspect-square flex justify-center items-center border-2 border-white shadow-sm">
                  <CareIcon
                    icon="d-health-worker"
                    className="size-8 text-green-700"
                  />
                </div>
                <div className="flex flex-col items-center">
                  <Button
                    variant="outline"
                    className="w-full text-xs md:text-sm border border-primary-600 text-primary-700 hover:text-primary-800 font-semibold"
                    onClick={() => navigate(`/login?mode=staff`)}
                  >
                    {t("staff_login")}
                  </Button>
                  <p className="text-xs mt-2 w-full text-center">
                    {t("staff_login_description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
